import styled, { css } from 'styled-components';
import { AllieText } from '../../../components/Text';
import { getCssVar } from '../../../common/utils';

export const bubbleStyles = css`
  position: absolute;
  left: -16px;
  top: 10px;
  background-color: white;
  border-radius: 50%;
  padding: 6px;
  z-index: 2;
`;

export const TimeGridContainer = styled.div`
  align-items: start;
  position: relative;
  height: 100%;
  overflow-x: auto;
  overflow-y: clip;
  border-top: 1px solid var(--allie-color-neutral-20);

  ion-backdrop {
    background: var(--allie-color-neutral-90);
    opacity: 0.2;
  }

  .show-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 3;
  }

  .timegrid-icon {
    height: 20px;
    svg {
      path {
        fill: var(--allie-color-neutral-60);
      }
    }
  }

  .row:first-child {
    background-color: var(--allie-color-light-main);
    border-bottom: 1px solid var(--allie-color-neutral-20);

    .label {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      &.center {
        justify-content: center;
        padding-right: 0;
      }

      &.minute-header {
        grid-column: span 5;
      }
    }

    > .label:not(:first-child)::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: -1px;
      height: 100%;
    }
  }

  .row {
    display: grid;
    grid-template-columns: 50px repeat(60, minmax(13px, 1fr)) 85px 85px;
    align-items: center;
    background: #fafafa;
    border-bottom: 1px solid var(--allie-color-neutral-20);

    .label:first-child,
    .label:last-child {
      background-color: var(--allie-color-light-main);
    }

    &.row-tv {
      grid-template-columns: 50px repeat(60, minmax(13px, 1fr)) 90px 80px;
    }

    &.wihout-produced {
      grid-template-columns: 50px repeat(60, minmax(13px, 1fr));
    }

    &.production-edit-mode {
      grid-template-columns: 50px repeat(60, minmax(13px, 1fr)) 170px;

      &.row-tv {
        grid-template-columns: 50px repeat(60, minmax(13px, 1fr)) 170px;
      }
    }
  }

  .cell {
    width: 100%;
    height: 100%;
    position: relative;

    &.border-white {
      border-left: 1px solid white;
    }

    &.is-clickable {
      cursor: pointer;
    }

    &.border-init-stop {
      border-left: 1px solid var(--allie-color-error-70);
    }

    &.border-init-warning {
      border-left: 1px solid var(--allie-color-warning-60);
    }

    &.data {
      display: flex;
      align-items: center;
      box-sizing: border-box;

      .icon-start-production {
        ${bubbleStyles}
      }

      .icon-without-reason {
        font-size: 20px;
        margin-left: 12px;

        &--single {
          margin-left: 1px;
        }

        &--stop {
          color: var(--allie-color-white);
        }

        &--speed_reduction {
          color: black;
        }
      }
    }

    /* BEV2-4 bulk assignment */
    &.mass-select-selectable {
      cursor: pointer;
    }

    &.mass-select-dimmed {
      opacity: 0.3;
      cursor: default;
    }

    &.mass-select-selected {
      /* Top/bottom only: a non-groupable stop is split into one cell per minute, all sharing
         the same id, so a full inset ring would draw a separate box around every minute
         instead of one outline around the stop. */
      box-shadow:
        inset 0 2px 0 0 var(--allie-color-primary-60),
        inset 0 -2px 0 0 var(--allie-color-primary-60);
    }
  }

  .mass-select-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    background-color: var(--allie-color-primary-60);
    color: var(--allie-color-white);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    line-height: 1;
    z-index: 1;
  }

  .label {
    text-align: right;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    &.center {
      text-align: center;
      background-color: var(--allie-color-light-main);
    }
  }

  .cells .label {
    text-align: left;
  }

  .tooltip {
    position: absolute;
    display: none;
    background: white;
    border: 1px solid #ccc;
    padding: 10px;
    z-index: 1;
    top: 50%;
    left: 100%;
    font-size: 18px;
    border-radius: 4px;
    min-width: 110px;
  }

  .tooltip span {
    display: block;
    font-size: 14px;
  }

  .tooltip .count {
    font-size: 18px;
    font-weight: 700;
  }

  .cell:hover .tooltip {
    display: block;
  }
`;

export const BubbleStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: white;
  cursor: pointer;

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const TableContent = styled.div<{ $compactView: boolean }>`
  display: grid;
  row-gap: 1px;
  ${(props) =>
    props.$compactView
      ? `
      overflow-y: auto;
      height: 100%;
      `
      : ''}
`;

export const TableHeaderText = styled(AllieText)`
  @media (min-width: ${() => getCssVar('--breakpoint-desktop-lg', '1600px')}) {
    font-size: 16px !important;
    line-height: 22px !important;
  }

  @media (min-width: ${() => getCssVar('--breakpoint-desktop-xl', '1920px')}) {
    font-size: 18px !important;
    line-height: 25px !important;
  }
`;

export const TableHeaderRate = styled(AllieText)`
  @media (min-width: ${() => getCssVar('--breakpoint-desktop-lg', '1600px')}) {
    font-size: 14px !important;
    line-height: 20px !important;
  }
`;
